import moment from 'moment-timezone';

import {generateUUID} from '@famly/stat_ts-utils_generate-uuid';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import type {
    Discount as PowerPageDiscount,
    Plan as PowerPagePlan,
} from 'web-app/react/routes/account/institution/finance/power-page/types';
import {type Either, isLeft, makeLeft, makeRight} from 'web-app/util/fp';
import {AutomaticInvoicingOccurrence, FinancePeriod} from 'web-app/util/graphql/api-types';
import {head, isNonEmpty} from 'web-app/util/non-empty-array';
import * as Warn from 'web-app/util/warnable';
import {type Warnable} from 'web-app/util/warnable';
import * as Failable from 'web-app/util/failable';

import type * as ApiTypes from '../api-types';
import type * as Types from '../types';

const transformPowerPagePlanToPlan = (
    plan: PowerPagePlan,
    billPayersBillingFrequencies: AutomaticInvoicingOccurrence[],
    genId?: () => string,
): Failable.Failable<{plan: Types.Plan; customDiscounts: Types.DiscountCustom[]}, string> => {
    const planPartsBillingFrequencies = plan.parts.map(part => part.billingScheme.id);
    const billingFrequency = transformBillingFrequenciesToPlanFrequency(
        billPayersBillingFrequencies,
        planPartsBillingFrequencies,
    );

    if (Failable.isFailed(billingFrequency) || billingFrequency.value === null) {
        return Failable.isFailed(billingFrequency) ? billingFrequency : Failable.fail('No billing frequency found.');
    }

    const discounts: Failable.Failable<Either<Types.DiscountCustom, string>[], string> = Failable.pick(
        Failable.all(
            plan.parts.flatMap(part =>
                part.discounts.map(
                    (discount): Failable.Failable<Either<Types.DiscountCustom, string>, string> =>
                        hasValue(discount.id)
                            ? Failable.resolve(makeRight(discount.id))
                            : Failable.map(transformCustomDiscount(discount, genId), makeLeft),
                ),
            ),
        ),
    );

    const customDiscountsAndIds = Failable.map(discounts, discounts => ({
        customDiscounts: discounts.map(discount => (isLeft(discount) ? discount.left : null)).filter(hasValue),
        discountIds: discounts.map(discount => (isLeft(discount) ? discount.left.id : discount.right)),
    }));

    if (Failable.isFailed(customDiscountsAndIds)) {
        return customDiscountsAndIds;
    }

    const planPricing = ((): Failable.Failable<Types.PlanPricing, string> => {
        if (hasValue(plan.pricingGroup)) {
            return Failable.resolve({type: 'manual', pricingGroup: plan.pricingGroup.pricingGroupId});
        }
        if (hasValue(plan.ruleGroupId)) {
            return Failable.resolve({type: 'automatic', ruleGroupId: plan.ruleGroupId});
        }
        return Failable.fail('No pricing group or automatic rule group found for selected plan');
    })();

    if (Failable.isFailed(planPricing)) {
        return planPricing;
    }

    return Failable.resolve({
        plan: {
            id: plan.planId,
            tuitions: [
                ...new Set(
                    plan.parts.flatMap(part => part.bookings.packages.map(pkg => pkg.sessionAvailability.session.id)),
                ),
            ].map(id => ({
                id,
                availability: plan.parts.flatMap(part =>
                    part.bookings.packages
                        .filter(pkg => pkg.sessionAvailability.session.id === id)
                        .map(pkg => ({
                            day: pkg.sessionAvailability.day,
                            end: pkg.timeRange.to,
                            start: pkg.timeRange.from,
                        })),
                ),
            })),
            fees: plan.parts.flatMap(part =>
                part.bookings.products.map(product => product.productAvailability.product.id),
            ),
            billingFrequency: billingFrequency.value,
            discounts: customDiscountsAndIds.value.discountIds,
            startDate: moment(plan.range.from),
            endDate: plan.range.to ? moment(plan.range.to) : null,
            firstUnbilledDate: null,
            type: isUnsupportedPlan(plan) ? 'unsupported-plan' : 'plan',
            pricing: planPricing.value,
        },
        customDiscounts: customDiscountsAndIds.value.customDiscounts,
    });
};

const isUnsupportedPlan = (plan: PowerPagePlan): boolean => {
    const hasSession = plan.parts.some(part => isNonEmpty(part.bookings.sessions));
    const hasFunding = plan.parts.some(part => hasValue(part.funding));
    const hasMultipleOfSameFee = plan.parts.some(part => {
        const productIds = part.bookings.products.map(product => product.productAvailability.product.id);
        return productIds.length !== new Set(productIds).size;
    });
    const hasAttendanceSchedule = plan.parts.some(
        part => hasValue(part.attendanceSchedule) && !part.attendanceSchedule.isFullYear,
    );
    const hasUnsupportedBillingProfile = plan.parts.some(
        part => part.billingScheme.id !== 'MONTHLY' && part.billingScheme.id !== 'ACTUALS_V2',
    );
    const hasDailyDiscounts = plan.parts.some(part =>
        part.discounts.some(discount => discount.period === FinancePeriod.DAILY),
    );
    const hasSurcharges = plan.parts.some(part => isNonEmpty(part.surcharges));

    return (
        hasMultipleOfSameFee ||
        hasSession ||
        hasFunding ||
        hasAttendanceSchedule ||
        hasUnsupportedBillingProfile ||
        hasDailyDiscounts ||
        hasSurcharges
    );
};

/**
 * Gets the billing frequency of the tuition of the plan.
 *
 * If there are multiple tuitions, gets the period of the first tuition with
 * a defined period.
 */
export const getTuitionBillingFrequency = (
    planTuitions: Types.Template['tuitions'],
    tuitionPeriodById: (tuitionId: string) => Types.BillingType | undefined,
): Warnable<Types.BillingType | undefined, string> => {
    const periods = planTuitions.map(tuition => tuitionPeriodById(tuition.id)).filter(hasValue);
    if (!isNonEmpty(periods)) {
        return Warn.warningMessage(undefined, 'No available tuition period.');
    }
    return Warn.resolve(head(periods));
};

/** Transforms a discount into a custom discount and assigns it a temporary id. */
const transformCustomDiscount = (
    discount: PowerPageDiscount,
    genId?: () => string,
): Failable.Failable<Types.DiscountCustom, string> => {
    const genIdLocal = hasValue(genId) ? genId : () => `tmp-${generateUUID()}`;
    return Failable.map(transformFinancePeriodToBillingType(discount.period), period => ({
        type: 'custom',
        id: genIdLocal(),
        title: discount.title,
        amount: discount.amount,
        ordering: discount.order,
        customDiscountType: discount.percent ? 'percentage' : 'amount',
        period,
    }));
};

const transformFinancePeriodToBillingType = (period: FinancePeriod): Failable.Failable<Types.BillingType, string> => {
    switch (period) {
        case FinancePeriod.MONTHLY:
            return Failable.resolve('monthly');
        case FinancePeriod.WEEKLY:
            return Failable.resolve('weekly');
        default:
            return Failable.fail(`Unsupported finance period: "${period}".`);
    }
};

const transformBillingFrequencyForBillPayer = (
    billPayerBillingFrequency: AutomaticInvoicingOccurrence | null,
): Warnable<Types.InitialBillingFrequency | null, string> => {
    switch (billPayerBillingFrequency) {
        case AutomaticInvoicingOccurrence.MONTHLY:
            return Warn.resolve('monthly');
        case AutomaticInvoicingOccurrence.BIWEEKLY:
            return Warn.resolve('biweekly');
        case AutomaticInvoicingOccurrence.WEEKLY:
            return Warn.resolve('weekly');
        case null:
        case undefined:
            // When there are no bill payers or the bill payer has no billing frequency.
            return Warn.resolve(null);
        default:
            return Warn.warningMessage(
                'monthly',
                `Unhandled billing frequency for bill payer: "${billPayerBillingFrequency}".`,
            );
    }
};

const transformBillingFrequenciesToPlanFrequency = (
    billPayersBillingFrequencies: AutomaticInvoicingOccurrence[],
    planPartsBillingFrequencies: string[],
): Failable.Failable<Types.InitialBillingFrequency | null, string> => {
    if (isNonEmpty(billPayersBillingFrequencies)) {
        const billPayerBillingFrequency = transformBillingFrequencyForBillPayer(head(billPayersBillingFrequencies));
        if (Warn.isResolved(billPayerBillingFrequency)) {
            return Failable.resolve(billPayerBillingFrequency.value);
        }
        return Failable.fail(billPayerBillingFrequency.warnings[0].warning);
    } else if (isNonEmpty(planPartsBillingFrequencies)) {
        switch (head(planPartsBillingFrequencies)) {
            case 'MONTHLY':
                return Failable.resolve('monthly');
            case 'ACTUALS_V2':
                return Failable.resolve('weekly');
            default:
                return Failable.fail(
                    `Unhandled billing frequency for plan part: "${head(planPartsBillingFrequencies)}".`,
                );
        }
    }
    return Failable.fail('No billing frequency found.');
};

const transformApiPlanToMaybeAlreadyBilledPlan = (plan: ApiTypes.ApiPlan): Types.MaybeAlreadyBilledPlan => {
    const effectiveChangeDate = plan.behaviors
        ?.find(behavior => behavior.id === 'ShowPlanWarnings')
        ?.payload?.warnings.find(warning => warning.metadata?.changeFrom)?.metadata?.changeFrom;
    const startDate = moment(plan.from);
    const endDate = plan.to ? moment(plan.to) : null;

    if (!hasValue(effectiveChangeDate)) {
        return {
            type: 'not-billed',
            id: plan.id,
            startDate,
            endDate,
        };
    }

    return {
        type: 'already-billed',
        id: plan.id,
        startDate,
        endDate,
        effectiveChangeDate: moment(effectiveChangeDate),
    };
};

export {transformApiPlanToMaybeAlreadyBilledPlan, transformBillingFrequencyForBillPayer, transformPowerPagePlanToPlan};
